import React, { useState, useContext } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import api from '../services/api';
import { LangContext } from '../App';

const T = {
  fr: {
    title: 'Réinitialiser le mot de passe',
    descLien:
      'Choisissez un nouveau mot de passe (8 caractères minimum). Vous serez ensuite redirigé vers la page de connexion.',
    descLienAbsent:
      'Lien de réinitialisation invalide. Demandez un nouveau lien depuis la page « Mot de passe oublié ».',
    nouveauMdp: 'Nouveau mot de passe',
    confirmerMdp: 'Confirmer le mot de passe',
    valider: 'Valider',
    enCours: 'Mise à jour…',
    succes: 'Mot de passe réinitialisé. Vous allez être redirigé vers la connexion…',
    erreurNonCorrespondance: 'Les deux mots de passe ne correspondent pas.',
    erreurCourt: 'Le mot de passe doit contenir au moins 8 caractères.',
    erreurServeur: 'Une erreur est survenue. Le lien est peut-être expiré.',
    versForgot: '← Demander un nouveau lien',
  },
  en: {
    title: 'Reset password',
    descLien:
      "Choose a new password (8 characters minimum). You'll be redirected to the sign-in page.",
    descLienAbsent: "Invalid reset link. Request a new one from the 'Forgot password' page.",
    nouveauMdp: 'New password',
    confirmerMdp: 'Confirm password',
    valider: 'Submit',
    enCours: 'Updating…',
    succes: "Password reset. You'll be redirected to sign in…",
    erreurNonCorrespondance: 'Passwords do not match.',
    erreurCourt: 'Password must be at least 8 characters.',
    erreurServeur: 'An error occurred. The link may have expired.',
    versForgot: '← Request a new link',
  },
};

const inputStyle = {
  width: '100%',
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.18)',
  borderRadius: '6px',
  color: 'var(--couleur-texte)',
  padding: '0.75rem 0.9rem',
  fontSize: '0.95rem',
};

const btnStyle = {
  width: '100%',
  padding: '0.85rem',
  background: 'var(--couleur-or)',
  color: '#000',
  border: 'none',
  borderRadius: '6px',
  fontWeight: 600,
  fontSize: '0.95rem',
  cursor: 'pointer',
};

export default function ResetPassword() {
  const { lang } = useContext(LangContext);
  const t = T[lang] || T.fr;
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();

  const [mdp, setMdp] = useState('');
  const [mdp2, setMdp2] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  // Token absent dans l'URL : on affiche un état d'erreur dès le mount.
  if (!token) {
    return (
      <div>
        <Header />
        <div
          style={{
            maxWidth: '460px',
            margin: '4rem auto 2rem',
            padding: '2rem',
            background: 'rgba(255,255,255,0.04)',
            borderRadius: '12px',
          }}
        >
          <h1 style={{ marginTop: 0 }}>{t.title}</h1>
          <p style={{ color: '#ff6b6b' }}>{t.descLienAbsent}</p>
          <p style={{ marginTop: '1.5rem', textAlign: 'center', fontSize: '0.86rem' }}>
            <Link to="/forgot-password" style={{ color: 'var(--couleur-or)' }}>
              {t.versForgot}
            </Link>
          </p>
        </div>
      </div>
    );
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (mdp.length < 8) {
      setError(t.erreurCourt);
      return;
    }
    if (mdp !== mdp2) {
      setError(t.erreurNonCorrespondance);
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password_hash: mdp });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      const msg = err?.response?.data?.error;
      setError(msg || t.erreurServeur);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Header />
      <div
        style={{
          maxWidth: '460px',
          margin: '4rem auto 2rem',
          padding: '2rem',
          background: 'rgba(255,255,255,0.04)',
          borderRadius: '12px',
        }}
      >
        <h1 style={{ marginTop: 0 }}>{t.title}</h1>
        <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.92rem' }}>{t.descLien}</p>

        {done ? (
          <div
            style={{
              padding: '1rem',
              background: 'rgba(46, 204, 113, 0.12)',
              border: '1px solid rgba(46, 204, 113, 0.4)',
              borderRadius: '6px',
              color: '#a3e9c5',
              fontSize: '0.92rem',
            }}
          >
            {t.succes}
          </div>
        ) : (
          <form onSubmit={onSubmit} style={{ display: 'grid', gap: '1rem', marginTop: '1.5rem' }}>
            <label>
              <div style={{ marginBottom: '0.35rem', fontSize: '0.86rem' }}>{t.nouveauMdp}</div>
              <input
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                value={mdp}
                onChange={(e) => setMdp(e.target.value)}
                style={inputStyle}
              />
            </label>
            <label>
              <div style={{ marginBottom: '0.35rem', fontSize: '0.86rem' }}>{t.confirmerMdp}</div>
              <input
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                value={mdp2}
                onChange={(e) => setMdp2(e.target.value)}
                style={inputStyle}
              />
            </label>
            {error && <div style={{ color: '#ff6b6b', fontSize: '0.86rem' }}>{error}</div>}
            <button type="submit" disabled={loading} style={btnStyle}>
              {loading ? t.enCours : t.valider}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
